import * as React from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

const defaultPageSizeOptions = [10, 20, 30, 40, 50];

export type PaginationBarProps = {
    pageIndex: number;
    pageSize: number;
    totalRecords: number;
    onPageChange: (newPageIndex: number) => void;
    onPageSizeChange: (newPageSize: number) => void;
    itemName?: string;
    pageSizeOptions?: number[];
};

export function PaginationBar({
    pageIndex,
    pageSize,
    totalRecords,
    onPageChange,
    onPageSizeChange,
    itemName = 'records',
    pageSizeOptions = defaultPageSizeOptions,
}: PaginationBarProps) {
    const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
    const [displayPageSize, setDisplayPageSize] = React.useState(pageSize);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const lastPageSizePropRef = React.useRef(pageSize);
    const totalPages = Math.ceil(totalRecords / displayPageSize) || 1;

    React.useEffect(() => {
        if (lastPageSizePropRef.current !== pageSize) {
            lastPageSizePropRef.current = pageSize;
            setDisplayPageSize(pageSize);
        }
    }, [pageSize]);

    React.useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    React.useEffect(() => {
        if (!pageSizeOptions.includes(displayPageSize) && pageSizeOptions.length > 0) {
            setDisplayPageSize(pageSizeOptions[0]);
            onPageSizeChange(pageSizeOptions[0]);
            onPageChange(1);
        }
    }, [displayPageSize, onPageChange, onPageSizeChange, pageSizeOptions]);

    const handlePageSizeSelect = (size: number) => {
        lastPageSizePropRef.current = size;
        setDisplayPageSize(size);
        onPageSizeChange(size);
        onPageChange(1);
        setIsDropdownOpen(false);
    };

    const startRecord = totalRecords === 0 ? 0 : (pageIndex - 1) * displayPageSize + 1;
    const endRecord = Math.min(pageIndex * displayPageSize, totalRecords);

    return (
        <div className="flex w-full items-center justify-between border-t border-gray-200 bg-white py-4 text-sm font-inter">
            <div className="font-medium text-slate-500">
                {totalRecords === 0
                    ? `No ${itemName} found`
                    : `Showing ${startRecord} to ${endRecord} of ${totalRecords} ${itemName}`}
            </div>

            <div className="flex items-center gap-4">
                <div className="relative" ref={dropdownRef}>
                    <button
                        type="button"
                        disabled={totalRecords === 0}
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        className="flex min-w-[115px] items-center justify-between rounded-full border border-gray-300 bg-white py-1.5 pl-4 pr-3 text-sm font-medium text-gray-700 outline-none transition-colors hover:border-gray-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-60"
                    >
                        <span>{displayPageSize} / page</span>
                        <ChevronDown
                            className={`ml-2 h-4 w-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''} ${totalRecords === 0 ? 'text-gray-300' : 'text-gray-500'}`}
                            strokeWidth={2.5}
                        />
                    </button>

                    {isDropdownOpen && (
                        <div className="absolute bottom-full left-0 z-50 mb-2 w-full origin-bottom animate-in overflow-hidden border border-gray-600 bg-white duration-100 fade-in zoom-in-95">
                            <ul className="max-h-60 overflow-auto">
                                {pageSizeOptions.map(size => (
                                    <li key={size}>
                                        <button
                                            type="button"
                                            className={`w-full px-4 py-2 text-left text-sm transition-colors hover:bg-gray-100 ${
                                                displayPageSize === size ? 'bg-indigo-600 font-semibold text-white' : 'text-gray-700'
                                            }`}
                                            onMouseDown={(event) => {
                                                event.preventDefault();
                                                handlePageSizeSelect(size);
                                            }}
                                        >
                                            {size} / page
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => onPageChange(pageIndex - 1)}
                        disabled={pageIndex === 1 || totalRecords === 0}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-500 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        <ChevronLeft className="h-4 w-4" strokeWidth={2.5} />
                    </button>

                    {Array.from({ length: totalPages }, (_, index) => index + 1)
                        .filter(page => {
                            if (totalPages <= 5) return true;
                            if (pageIndex <= 3) return page <= 5;
                            if (pageIndex >= totalPages - 2) return page > totalPages - 5;
                            return page >= pageIndex - 2 && page <= pageIndex + 2;
                        })
                        .map(page => (
                            <button
                                key={page}
                                type="button"
                                onClick={() => onPageChange(page)}
                                disabled={totalRecords === 0}
                                className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-colors ${
                                    pageIndex === page
                                        ? 'bg-[#5b45c2] text-white'
                                        : 'bg-transparent text-gray-600 hover:bg-gray-100'
                                } disabled:cursor-not-allowed disabled:opacity-50`}
                            >
                                {page}
                            </button>
                        ))}

                    <button
                        type="button"
                        onClick={() => onPageChange(pageIndex + 1)}
                        disabled={pageIndex === totalPages || totalRecords === 0}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-500 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
                    </button>
                </div>
            </div>
        </div>
    );
}
